import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  ApiDownloadButton,
  LoadingIndicator,
  Pagination
} from 'components/common';
import { PageEmptyIndicator, PageSectionHeader } from 'components/page';
import { AchievementsAvailableTile } from '.';

import type { ViewProps } from 'types';
import type { GetAchievementsRes } from 'shared-types/responses';

const AchievementsViewAvailable = ({
  description,
  hideIfNoData = false,
  isHomeView = false,
  limitPerPage = 12,
  onNoData = null,
  queryParams = '',
  title
}: ViewProps) => {
  const { messages, user } = useReduxState();

  const router = useRouter();

  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [queryParams]);

  const { data, loading, updateData } = useApiData<GetAchievementsRes>({
    url:
      `/admin/achievements?type=available&achievementsPerPage=${limitPerPage}&page=${page}` +
      queryParams
  });

  useEffect(() => {
    const hasData = !loading && Boolean(data?.achievements?.length);
    if (!loading && onNoData) onNoData(hasData);
  }, [data, loading]);

  const navigateToAchievementPage = (id: number) =>
    router.push(`/achievements/${id}`, `/achievements/${id}`);

  return (
    <div className="achievements-list">
      {loading ? (
        <LoadingIndicator />
      ) : data?.achievements?.length ? (
        <>
          <PageSectionHeader
            description={description}
            title={title || messages.achievementsAvailableAchievements}
            titleCount={data?.achievementsCount}
          >
            {isHomeView || !user?.roles.includes('superadmin') ? null : (
              <ApiDownloadButton
                category="success"
                disabled={!data?.achievementsIds.length}
                filename={`${messages.achievementsAvailableAchievements}.xlsx`}
                icon="file-excel"
                packet={{ achievementIds: data?.achievementsIds || [] }}
                size="header"
                text={messages.tasksExportTasksData}
                url="/admin/achievements/excel-export"
              />
            )}
          </PageSectionHeader>

          <div className="achievements">
            {data.achievements.map((achievement) => (
              <div
                className="achievement"
                key={achievement.id}
                onClick={() => navigateToAchievementPage(achievement.id)}
                onKeyDown={(ev: React.KeyboardEvent) => {
                  if (ev.target !== ev.currentTarget) return;
                  // check if enter or spacebar
                  if (ev.keyCode === 13 || ev.keyCode === 32) {
                    ev.preventDefault();
                    navigateToAchievementPage(achievement.id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <AchievementsAvailableTile
                  achievement={achievement}
                  updateAchievements={updateData}
                />
              </div>
            ))}
          </div>

          {data?.achievements?.length && data?.pages > 1 && !isHomeView ? (
            <Pagination
              currentPage={page}
              numberOfPages={data?.pages || 1}
              onPageChange={({ page }) => setPage(page)}
            />
          ) : null}
        </>
      ) : hideIfNoData ? null : (
        <>
          <PageSectionHeader
            description={description}
            title={title || messages.achievementsAvailableAchievements}
            titleCount={0}
          />

          <PageEmptyIndicator
            icon="trophy"
            title={messages.achievementsEmpty}
            description={messages.achievementsEmpty}
          />
        </>
      )}

      <style jsx>{`
        .achievements {
          display: grid;
          gap: 30px;
          grid-template-columns: repeat(3, 1fr);
          grid-template-rows: auto;
          height: 100%;
        }
        @media (max-width: ${breakpoints.large - 1}px) {
          .achievements {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .achievements {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default AchievementsViewAvailable;
